import { ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { SurveyCountMetric } from '@/types/dashboard.types';
import { DateRange } from '@/types/dashboard.types';
import { BarChart } from './bar-chart';
import {
  getChangeLabel,
  getChartLabel,
  getValueSublabel,
} from './analytics-card-helpers';

interface SurveyCountCardProps {
  title: string;
  metric: SurveyCountMetric;
  dateRange: DateRange;
  onNavigate: () => void;
  tooltip?: string;
}

export function SurveyCountCard({
  title,
  metric,
  dateRange,
  onNavigate,
  tooltip,
}: SurveyCountCardProps) {
  const isAllData = dateRange === DateRange.ALL_DATA;
  const isIncrease = metric.isIncrease ?? false;
  const hasChange = metric.absoluteChange !== undefined;
  const hasLast7DaysData = metric.last7DaysCount !== undefined;
  const isZeroChange = metric.absoluteChange === 0;

  // For all_data: green when there were surveys in the last 7 days (an
  // increase), gray only when there were none
  // For last_7_days/last_30_days: use gray for zero change, green/red for actual changes
  const changeColor = isAllData
    ? (metric.last7DaysCount ?? 0) > 0
      ? 'analytics-card-change-increase'
      : 'analytics-card-change-neutral'
    : isZeroChange
    ? 'analytics-card-change-neutral'
    : isIncrease
    ? 'analytics-card-change-increase'
    : 'analytics-card-change-decrease';

  const icon = tooltip ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="analytics-card-external-link" onClick={onNavigate}>
            <ExternalLink />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <div className="analytics-card-external-link" onClick={onNavigate}>
      <ExternalLink />
    </div>
  );

  const cardContent = (
    <Card className="analytics-card">
      <CardContent className="analytics-card-content">
        {icon}
        <div className="analytics-card-title">{title}</div>
        <div className="analytics-card-value-container">
          <span className="analytics-card-value">{metric.value}</span>
          {isAllData
            ? // All data range: show last 7 days count with + sign
              hasLast7DaysData && (
                <div className={`analytics-card-change-row ${changeColor}`}>
                  <span className="analytics-card-change-value">
                    +{metric.last7DaysCount}
                  </span>
                  <span className="analytics-card-change-label">
                    {getChangeLabel(dateRange)}
                  </span>
                </div>
              )
            : // Last 7/30 days: show increase/decrease with percentage
              hasChange && (
                <div className={`analytics-card-change-row ${changeColor}`}>
                  <span className="analytics-card-change-value">
                    {isZeroChange || isIncrease ? '↑' : '↓'}{' '}
                    {metric.absoluteChange}
                  </span>
                  <span className="analytics-card-change-percentage">
                    ({isIncrease || isZeroChange ? '+' : '-'}
                    {metric.percentChange!.toFixed(1)}%)
                  </span>
                  <span className="analytics-card-change-label">
                    {getChangeLabel(dateRange)}
                  </span>
                </div>
              )}
        </div>
        <div className="analytics-card-sublabel">
          {getValueSublabel(dateRange, 'count')}
        </div>
        <BarChart
          trendline={metric.trendline || []}
          label={getChartLabel(
            dateRange,
            metric.trendlinePeriodLabel,
            metric.trendline || [],
            'counts',
          )}
          scaleType="count"
          dateRange={dateRange}
          periodLabel={metric.trendlinePeriodLabel}
        />
      </CardContent>
    </Card>
  );

  return cardContent;
}
